import { useEffect, useMemo, useRef, useState } from "react";
import { SourceTypeIds } from "../SourceTypeIds";
import {
  parseConnectionConfiguration,
  updateConnectionConfiguration,
} from "../source-settings/SourceSettingsConfiguration";
import type { SourceSettingsModel } from "../source-settings/SourceSettingsApiAccess";
import { TrelloSourceSettingsApi } from "./TrelloSourceSettings.Api";
import type { TrelloSourceSettingsViewModel } from "./TrelloSourceSettings.ViewModel.types";
import type {
  TrelloBoard,
  TrelloConfiguration,
  TrelloLabel,
  TrelloList,
} from "./TrelloSourceSettings.types";

const NONE_OPTION = "__none";

const normalizeId = (value: unknown): string => String(value ?? "");

export const useTrelloSourceSettingsViewModel = (
  model: SourceSettingsModel,
): TrelloSourceSettingsViewModel => {
  const editingConnectionId = model.editingConnection.id;
  const onUpdateRef = useRef(model.onUpdate);
  const sourceSettingsApiRef = useRef(model.sourceSettingsApi);
  const editingConnectionRef = useRef(model.editingConnection);
  const initialisedConnectionIdRef = useRef<string | null>(null);
  const autoSelectedBoardForConnectionRef = useRef<string | null>(null);
  const seededListsForBoardRef = useRef<string | null>(null);
  const previousProfileIdentityRef = useRef(
    `${model.editingConnection.profileId ?? ""}|${model.editingConnection.profileName ?? ""}|${model.editingConnection.isAuthorised ? "1" : "0"}`,
  );

  const [boards, setBoards] = useState<TrelloBoard[]>([]);
  const [lists, setLists] = useState<TrelloList[]>([]);
  const [labels, setLabels] = useState<TrelloLabel[]>([]);
  const [boardsLoaded, setBoardsLoaded] = useState(false);
  const [listsLoaded, setListsLoaded] = useState(false);
  const [labelsLoaded, setLabelsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    onUpdateRef.current = model.onUpdate;
  }, [model.onUpdate]);

  useEffect(() => {
    sourceSettingsApiRef.current = model.sourceSettingsApi;
  }, [model.sourceSettingsApi]);

  useEffect(() => {
    editingConnectionRef.current = model.editingConnection;
  }, [model.editingConnection]);

  const isVisible =
    model.editingConnection.sourceTypeId.trim().toLowerCase() ===
      SourceTypeIds.Trello && model.editingConnection.isAuthorised;

  const configuration = useMemo(
    () =>
      parseConnectionConfiguration<TrelloConfiguration>(
        model.editingConnection.configuration,
      ),
    [model.editingConnection.configuration],
  );
  const selectedBoardId = normalizeId(configuration.board?.id);

  useEffect(() => {
    if (!isVisible) {
      initialisedConnectionIdRef.current = null;
      autoSelectedBoardForConnectionRef.current = null;
      seededListsForBoardRef.current = null;
      return;
    }

    if (initialisedConnectionIdRef.current === editingConnectionId) {
      return;
    }

    initialisedConnectionIdRef.current = editingConnectionId;

    updateConnectionConfiguration<TrelloConfiguration>(
      editingConnectionRef.current,
      onUpdateRef.current,
      (cfg) => ({
        ...cfg,
        lists: cfg.lists ?? [],
        listsToLoad: cfg.listsToLoad ?? [],
      }),
      { isDefault: true },
    );
  }, [editingConnectionId, isVisible]);

  useEffect(() => {
    const currentProfileIdentity = `${model.editingConnection.profileId ?? ""}|${model.editingConnection.profileName ?? ""}|${model.editingConnection.isAuthorised ? "1" : "0"}`;
    const previousProfileIdentity = previousProfileIdentityRef.current;
    previousProfileIdentityRef.current = currentProfileIdentity;

    if (!isVisible || previousProfileIdentity === currentProfileIdentity) {
      return;
    }

    setBoards([]);
    setLists([]);
    setLabels([]);
    autoSelectedBoardForConnectionRef.current = null;
    seededListsForBoardRef.current = null;

    updateConnectionConfiguration<TrelloConfiguration>(
      editingConnectionRef.current,
      onUpdateRef.current,
      (cfg) => ({
        ...cfg,
        board: undefined,
        lists: [],
        labelFilter: [],
        doneList: undefined,
        listsToLoad: [],
        listForTaskCreation: undefined,
        connectionLabels: {},
      }),
    );
  }, [
    isVisible,
    model.editingConnection.isAuthorised,
    model.editingConnection.profileId,
    model.editingConnection.profileName,
  ]);

  useEffect(() => {
    const loadBoards = async (): Promise<void> => {
      if (!isVisible || !model.boardId || !editingConnectionId) {
        setBoards([]);
        setBoardsLoaded(true);
        return;
      }

      const trelloApi =
        sourceSettingsApiRef.current?.tryGetSourceApi<TrelloSourceSettingsApi>(
          SourceTypeIds.Trello,
        );
      if (!trelloApi) {
        setBoards([]);
        setBoardsLoaded(true);
        return;
      }

      setBoardsLoaded(false);
      setHasError(false);
      try {
        const nextBoards = await trelloApi.getBoards(
          model.boardId,
          editingConnectionId,
        );
        setBoards(nextBoards);

        if (
          !selectedBoardId &&
          nextBoards.length > 0 &&
          autoSelectedBoardForConnectionRef.current !== editingConnectionId
        ) {
          autoSelectedBoardForConnectionRef.current = editingConnectionId;
          updateConnectionConfiguration<TrelloConfiguration>(
            editingConnectionRef.current,
            onUpdateRef.current,
            (cfg) =>
              normalizeId(cfg.board?.id)
                ? cfg
                : {
                    ...cfg,
                    board: nextBoards[0],
                    lists: [],
                    labelFilter: [],
                    doneList: undefined,
                    listsToLoad: [],
                    listForTaskCreation: undefined,
                    connectionLabels: {},
                  },
            { isDefault: true },
          );
        }
      } catch {
        setBoards([]);
        setHasError(true);
      } finally {
        setBoardsLoaded(true);
      }
    };

    void loadBoards();
  }, [editingConnectionId, isVisible, model.boardId, selectedBoardId]);

  useEffect(() => {
    const loadLists = async (): Promise<void> => {
      if (
        !isVisible ||
        !model.boardId ||
        !editingConnectionId ||
        !selectedBoardId
      ) {
        setLists([]);
        setListsLoaded(true);
        return;
      }

      const trelloApi =
        sourceSettingsApiRef.current?.tryGetSourceApi<TrelloSourceSettingsApi>(
          SourceTypeIds.Trello,
        );
      if (!trelloApi) {
        setLists([]);
        setListsLoaded(true);
        return;
      }

      setListsLoaded(false);
      setHasError(false);
      try {
        const nextLists = await trelloApi.getLists(
          model.boardId,
          editingConnectionId,
          selectedBoardId,
        );
        setLists(nextLists);

        const seedKey = `${editingConnectionId}:${selectedBoardId}`;
        if (
          (configuration.listsToLoad?.length ?? 0) === 0 &&
          nextLists.length > 1 &&
          seededListsForBoardRef.current !== seedKey
        ) {
          seededListsForBoardRef.current = seedKey;
          updateConnectionConfiguration<TrelloConfiguration>(
            editingConnectionRef.current,
            onUpdateRef.current,
            // Seeds only the board the lists were loaded for, and only what
            // is missing, so it is safe to apply to the saved settings too.
            (cfg) =>
              normalizeId(cfg.board?.id) !== selectedBoardId ||
              (cfg.listsToLoad?.length ?? 0) > 0
                ? cfg
                : {
                    ...cfg,
                    lists: nextLists,
                    listsToLoad: nextLists.slice(0, nextLists.length - 1),
                    doneList: cfg.doneList ?? nextLists[nextLists.length - 1],
                    listForTaskCreation: cfg.listForTaskCreation ?? nextLists[0],
                  },
            { isDefault: true },
          );
        } else {
          // Keeps the stored copy of the board's lists current. Not a choice
          // the user made, so it is saved like a default.
          updateConnectionConfiguration<TrelloConfiguration>(
            editingConnectionRef.current,
            onUpdateRef.current,
            (cfg) =>
              normalizeId(cfg.board?.id) !== selectedBoardId
                ? cfg
                : { ...cfg, lists: nextLists },
            { isDefault: true },
          );
        }
      } catch {
        setLists([]);
        setHasError(true);
      } finally {
        setListsLoaded(true);
      }
    };

    void loadLists();
  }, [
    configuration.listsToLoad?.length,
    editingConnectionId,
    isVisible,
    model.boardId,
    selectedBoardId,
  ]);

  useEffect(() => {
    const loadLabels = async (): Promise<void> => {
      if (
        !isVisible ||
        !model.boardId ||
        !editingConnectionId ||
        !selectedBoardId
      ) {
        setLabels([]);
        setLabelsLoaded(true);
        return;
      }

      const trelloApi =
        sourceSettingsApiRef.current?.tryGetSourceApi<TrelloSourceSettingsApi>(
          SourceTypeIds.Trello,
        );
      if (!trelloApi) {
        setLabels([]);
        setLabelsLoaded(true);
        return;
      }

      setLabelsLoaded(false);
      setHasError(false);
      try {
        setLabels(
          await trelloApi.getLabels(
            model.boardId,
            editingConnectionId,
            selectedBoardId,
          ),
        );
      } catch {
        setLabels([]);
        setHasError(true);
      } finally {
        setLabelsLoaded(true);
      }
    };

    void loadLabels();
  }, [editingConnectionId, isVisible, model.boardId, selectedBoardId]);

  const statusReferenceErrorText = useMemo(() => {
    if (!configuration.doneList || !configuration.listsToLoad?.length) {
      return "";
    }

    return configuration.listsToLoad.some(
      (list) =>
        normalizeId(list.id) === normalizeId(configuration.doneList?.id),
    )
      ? model.t(
          "board.connections.dialog.sourceSettings.trello.statusReferenceError",
        )
      : "";
  }, [configuration.doneList, configuration.listsToLoad, model]);

  return {
    isVisible,
    boards,
    lists,
    labels,
    boardsLoaded,
    listsLoaded,
    labelsLoaded,
    hasError,
    selectedBoardId: selectedBoardId || NONE_OPTION,
    selectedBoardName:
      selectedBoardId === NONE_OPTION
        ? ""
        : boards.find((board) => normalizeId(board.id) === selectedBoardId)
            ?.name || "",
    selectedListsToLoadIds: (configuration.listsToLoad ?? []).map((list) =>
      normalizeId(list.id),
    ),
    selectedDoneListId: normalizeId(configuration.doneList?.id) || NONE_OPTION,
    selectedDoneListName:
      normalizeId(configuration.doneList?.id) === ""
        ? ""
        : lists.find(
            (list) =>
              normalizeId(list.id) === normalizeId(configuration.doneList?.id),
          )?.name || "",
    selectedLabelFilterIds: (configuration.labelFilter ?? []).map((label) =>
      normalizeId(label.id),
    ),
    assignedToMe: configuration.assignedToMe ?? false,
    statusReferenceErrorText,
    noneOptionValue: NONE_OPTION,
    onSelectBoard: (boardId?: string) => {
      const selectedId = String(boardId || NONE_OPTION);
      const selectedBoard = boards.find(
        (board) => normalizeId(board.id) === selectedId,
      );

      updateConnectionConfiguration<TrelloConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          board: selectedId === NONE_OPTION ? undefined : selectedBoard,
          lists: [],
          labelFilter: [],
          doneList: undefined,
          listsToLoad: [],
          listForTaskCreation: undefined,
          connectionLabels: {},
        }),
      );
    },
    onSelectListsToLoad: (listIds: string[]) => {
      const selected = lists.filter((list) =>
        listIds.includes(normalizeId(list.id)),
      );

      updateConnectionConfiguration<TrelloConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          listsToLoad: selected,
        }),
      );
    },
    onSelectDoneList: (listId?: string) => {
      const selectedId = String(listId || NONE_OPTION);
      const selectedList = lists.find(
        (list) => normalizeId(list.id) === selectedId,
      );

      updateConnectionConfiguration<TrelloConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          doneList: selectedId === NONE_OPTION ? undefined : selectedList,
        }),
      );
    },
    onSelectLabelFilter: (labelIds: string[]) => {
      const selected = labels.filter((label) =>
        labelIds.includes(normalizeId(label.id)),
      );

      updateConnectionConfiguration<TrelloConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          labelFilter: selected,
        }),
      );
    },
    onToggleAssignedToMe: (value: boolean) => {
      updateConnectionConfiguration<TrelloConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          assignedToMe: value,
        }),
      );
    },
  };
};
